import {api, RequestParam} from "@/shared/api";
import {AssignmentPanel} from "@/shared/admin/assignmentPanel";
import * as EntityLinks from "@/shared/entityLinks";
import {FormUtil} from "@/shared/forms/formUtil";
import {UserAssignmentPanel} from "@/shared/admin/basicSameTypeAssignmentPanel";
import {AttachmentGallery} from "@/shared/ui/attachmentGallery";
import * as AttachmentUtil from "@/shared/attachments/attachmentUtil";
import {BudgetCalculator} from "@/shared/budget/budgetCalculator";
import {LocationPanel, LocationPanelData} from "@/shared/locationPanel";
import {TaskUserGroupAssignmentPanel} from "@/shared/admin/userGroupAssignmentPanel";
import {htmlEscape} from "@/shared/utils/string";
import {promiseNow} from "@/shared/utils/promise";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {debounce, toggleNavigationTabs} from "@/shared/utils/misc";
import {SecurityStatus} from "@/shared/securityStatus";
import {
	formatDate,
	formatDecimalNumber,
	formatTime,
	getISODate,
	getISOTime,
	normalizeTimeInput,
	parseDateInput,
	parseDecimalNumber,
	parseISODate,
	parseISOTime,
	parseTimeInput,
	Temporal
} from "@/shared/utils/datefunctions";
import {
	AssignedGroup,
	AssignedUser,
	ErrorResponse,
	PartialUser,
	Task,
	TaskAttachmentDownloadUrlResponse,
	TaskDetailsResponse,
	TaskLocationData
} from "@/shared/timr";
import {BudgetPlanningType, RecordingLockType} from "@/shared/timrTypes";
import {toAssignableUser} from "@/shared/admin/toAssignableUserHelper";

export enum TaskDialogMode {
	Create = "Create",
	Edit = "Edit",
	Subtask = "Subtask"
}

export interface TaskAdministrationDialogConfig {
	dialogId: string;
	size?: BootstrapModal.ModalSize;
	billingAddressCountryId?: number;
}

export class TaskAdministrationDialog {
	public static EVENT_TASK_SAVED = "taskAdministrationDialogEventTaskSaved";
	public static EVENT_TASK_DELETED = "taskAdministrationDialogEventTaskDeleted";

	private static readonly MAX_ATTACHMENT_COUNT = 10;
	private static readonly MAX_ATTACHMENT_SIZE = 20_000_000; // 20MB

	private static TITLE: {
		[key in keyof typeof TaskDialogMode]: string
	} = {
		Create: "record.task.create",
		Edit: "record.task.edit",
		Subtask: "record.task.create.subtask",
	}

	protected dom: JQuery;
	protected dialog: BootstrapModal.Modal;
	protected taskDialogMode = TaskDialogMode.Create;
	protected leaderAssignmentPanel?: AssignmentPanel<AssignedUser, PartialUser>;
	protected taskAssignmentPanel: TaskUserGroupAssignmentPanel;
	protected budgetCalculator: BudgetCalculator;
	protected isTaskLeader = false;
	protected hoursPlannedForSubtasks = -1;
	protected budgetPlannedForSubtasks = -1;
	protected taskId: number;
	protected taskUUID: string;
	protected originalBudgetPlanningType: BudgetPlanningType;
	protected originalHourlyRate = 0;
	protected hourlyRateParent: number;
	protected budgetPlanningTypeParent: BudgetPlanningType;
	protected budgetCountNotBillableHoursParent: boolean;
	protected taskPath: string;

	private locationPanel?: LocationPanel;
	private parentTaskLocationData?: TaskLocationData;
	private locationInherited = true;

	private attachmentsLoaded: boolean;
	private taskAttachmentGallery?: AttachmentGallery
	private inheritedAttachmentGallery?: AttachmentGallery

	constructor(public config: TaskAdministrationDialogConfig) {
		this.config.size = this.config.size || "modal-xl";
		this.dom = $(this.config.dialogId);
		this.init();
	}

	protected init() {
		this.initDialog();
		if (SecurityStatus.isCorporateFeaturesAllowed()) {
			this.initTaskProjectLeaders();
		}
		this.initTaskAssignments();
		this.initForm();
		this.initBudgetCalculator();

		// Resize assignment panels on window size change
		$(window).resize(debounce(() => this.resizeAssignmentPanels(), this, 50));

		$('a[data-toggle="tab"]').on("shown.bs.tab", e => {
			const tabId = $(e.target).attr("href");
			if (tabId === "#taskAdministrationProjectLeaders") {
				this.leaderAssignmentPanel?.updateView();
			} else if (tabId === "#taskAdministrationUserAssignments") {
				this.taskAssignmentPanel.updateView();
			} else if (tabId === "#taskAdministrationPosition") {
				this.locationPanel?.refreshMap();
			} else if (tabId === "#taskAdministrationAttachments") {
				this.updateAttachmentsTab();
			}
		});

		const onEditModeChanged = () => {
			const editMode = this.leaderAssignmentPanel?.isInEditMode()
				|| this.taskAssignmentPanel.isInEditMode();

			this.dialog.toggleButton("saveButton", !editMode);
			this.dialog.toggleButton("cancelButton", !editMode);
			this.dialog.toggleButton("deleteButton", !editMode && this.taskDialogMode === TaskDialogMode.Edit);
			toggleNavigationTabs("#tabNavigation", editMode);
		};

		this.leaderAssignmentPanel?.onEditModeChanged(onEditModeChanged);
		this.taskAssignmentPanel.onEditModeChanged(onEditModeChanged);

		this.dom.find("#taskEarliestStartTime, #taskLatestEndTime").change(function (e) {
			const element = $(e.target);
			element.val(formatTime(parseTimeInput(element.val() as string)));
		});

		this.dom.find("#taskBookable").change(e => {
			const taskBookable = $(e.target).val() === "true";
			this.dom.find("#taskEarliestStartTime, #taskLatestEndTime").prop("disabled", !taskBookable);
			this.dom.find("#descriptionRequiredDiv").toggle(taskBookable);
			this.updateAIValidationPromptVisibility();
		});

		this.dom.find("#taskName").on("input", e => {
			const name = $(e.target).val() as string;
			this.dialog.setTitle(this.buildTitle(name), true);
			this.updateTaskLink(name);
		});

		this.initAIValidationPromptDropdown();
		this.initPositionTabFields();

		this.taskAttachmentGallery = document.getElementById("taskAdministrationAttachmentGallery") as AttachmentGallery
		this.inheritedAttachmentGallery = document.getElementById("taskAdministrationInheritedAttachmentGallery") as AttachmentGallery

		this.taskAttachmentGallery?.setFileSelectorOptions({
			maxFiles: TaskAdministrationDialog.MAX_ATTACHMENT_COUNT,
			maxFileSize: TaskAdministrationDialog.MAX_ATTACHMENT_SIZE,
		});
	}

	protected initForm() {
		this.initFields();
		this.initTaskLockDateClearButton();
		this.initCheckboxes();
	}

	protected initDialog() {
		this.dialog = new BootstrapModal.Modal({
			size: this.config.size,
			title: this.buildTitle(),
			escapeText: false,
			message: this.dom,
			buttons: [{
				text: "dialog.save",
				action: (modal, button) => {
					this.save(button);
				},
				css: "saveButton btn-primary"
			}, {
				text: "dialog.delete",
				action: () => {
					this.deleteTask();
				},
				css: "deleteButton btn-outline-danger",
				left: true
			}, {
				text: "dialog.cancel",
				action: () => {
					this.closeDialog();
				},
				css: "cancelButton btn-default"
			}],
			onOpen: () => {
				this.resizeAssignmentPanels();
				this.updateAllAssignedWarningStyle();
				$("#taskName").focus();
			},
			onClose: () => {
				this.leaderAssignmentPanel?.setEditMode(false);
				this.taskAssignmentPanel.setEditMode(false);
				FormUtil.removeAllFeedback($("#taskAdministrationDialogForm"), true);
				FormUtil.switchTabNavigationToFirst($("ul.dialog-nav-tabs"));
			},
			preventCloseIfLightboxOpen: true
		});
	}

	protected initBudgetCalculator() {
		this.budgetCalculator = new BudgetCalculator(
			(hoursPlanned, userHoursPlanned) => {
				this.dom.find("#hoursPlanned").val(formatDecimalNumber(hoursPlanned));
				this.checkHoursPlannedExceedanceWarning();
				this.updateHoursPlannedTotal(this.budgetCalculator.getHoursPlanned());
				this.updateFixedBudgetInfo(hoursPlanned, userHoursPlanned);
			},
			(hourlyRate) => {
				this.dom.find("#hourlyRate").val(formatDecimalNumber(hourlyRate));
			},
			(budgetPlanned) => {
				this.dom.find("#budgetPlanned").val(formatDecimalNumber(budgetPlanned));
				this.updateBudgetPlannedTotal(this.budgetCalculator.getBudgetPlanned());
			},
			() => {
				this.checkHoursPlannedExceedanceWarning();
			},
			(hoursPlanned, userHoursPlanned) => {
				this.taskAssignmentPanel.updateView();
				this.updateFixedBudgetInfo(hoursPlanned, userHoursPlanned);
			}
		);
		if (this.dom.find("#budgetPlanningTypeSelect").length) {
			this.dom.find("#budgetPlanningTypeSelect").change(() => {
				const budgetPlanningType = this.getBudgetPlanningType();
				this.handleBudgetPlanningType(budgetPlanningType);
				this.applyBudgetPlanningTypeToCalculator(budgetPlanningType);
				this.taskAssignmentPanel.updateView();
			});
		}
		const hoursPlannedSelector = this.dom.find("#hoursPlanned");
		if (hoursPlannedSelector.length) {
			hoursPlannedSelector.change(() => {
				const hoursPlannedString = this.dom.find("#hoursPlanned").val() as string;
				this.budgetCalculator.setHoursPlanned(hoursPlannedString ? parseDecimalNumber(hoursPlannedString) : 0);
			});
		}
		const hourlyRateSelector = this.dom.find("#hourlyRate");
		if (hourlyRateSelector.length) {
			hourlyRateSelector.change(() => {
				const hourlyRateString = this.dom.find("#hourlyRate").val() as string;
				this.budgetCalculator.setHourlyRate(hourlyRateString ? parseDecimalNumber(hourlyRateString) : 0);
			});
		}
		const budgetPlannedSelector = this.dom.find("#budgetPlanned");
		if (budgetPlannedSelector.length) {
			budgetPlannedSelector.change(() => {
				const budgetPlannedString = this.dom.find("#budgetPlanned").val() as string;
				this.budgetCalculator.setBudgetPlanned(budgetPlannedString ? parseDecimalNumber(budgetPlannedString) : null);
			});
		}
	}

	protected initFields() {
		this.dom.find("#taskId").val("");
		this.dom.find("#taskVersion").val("0");
		this.dom.find("#parentTaskId").val("");
		this.dom.find("#taskName").val("");
		this.dom.find("#taskDescription").val("");
		this.dom.find("#taskExternalComments").val("");
		this.dom.find("#taskAiValidationPrompt").val("");
		this.dom.find("#taskStart").val("");
		this.dom.find("#taskEnd").val("");
		this.dom.find("#taskEarliestStartTime").val("");
		this.dom.find("#taskEarliestStartTimeField").val("");
		this.dom.find("#taskLatestEndTime").val("");
		this.dom.find("#taskLatestEndTimeField").val("");
		this.dom.find("#taskEarliestStartTime, #taskLatestEndTime").prop("disabled", false);
		this.dom.find("#taskLockDate").val("");
		this.dom.find("#taskExternalId").val("");
		this.dom.find(`select[name="bookable"]`).val("" + true);
		this.dom.find("#descriptionRequiredDiv").show();
		this.dom.find("#customTaskField1").val("");
		this.dom.find("#customTaskField2").val("");
		this.dom.find("#customTaskField3").val("");

		this.dom.find("#taskEarliestStartTime").attr("placeholder", formatTime(Temporal.PlainTime.from({hour: 0, minute: 0})));
		this.dom.find("#taskLatestEndTime").attr("placeholder", formatTime(Temporal.PlainTime.from({hour: 23, minute: 59})));

		this.locationPanel?.reset();
	}

	protected initTaskLockDateClearButton() {
		this.dom.find("#taskLockDateClearButton").click(() => {
			this.dom.find("#taskClearLock").val(String(true));
			this.setTaskLockDate(null);
		});
	}

	protected initCheckboxes() {
		const checkbox = this.dom.find("input:checkbox");
		checkbox.change(e => {
			const element = $(e.target);
			const hiddenInput = element.closest(".checkbox-inline").find("input[type=hidden]");
			if (element) {
				hiddenInput.val(element.is(":checked").toString());
			}

			// Update AI validation prompt visibility when description required changes
			if (element.attr("name") === "descriptionRequired") {
				this.updateAIValidationPromptVisibility();
			}
		});
		this.dom.find(`input[name="billable"]`).prop("checked", true);
		this.dom.find(`input[name="descriptionRequired"]`).prop("checked", false);
		this.dom.find(`input[name="budgetCountNotBillableHours"][type="checkbox"]`).prop("checked", false);
		// Set hidden field if budgetCountNotBillableHours setting is not configurable on per task basis
		this.dom.find(`input[name="budgetCountNotBillableHours"][type="hidden"]`).val("");

		// Initialize AI validation prompt visibility
		this.updateAIValidationPromptVisibility();
	}

	private initAIValidationPromptDropdown() {
		// Handle "Accept" buttons in the AI validation prompt examples dropdown
		this.dom.find(".ai-prompt-example-btn").click(e => {
			e.preventDefault();
			const prompt = $(e.target).closest(".ai-prompt-example").find(".ai-prompt-example-text").text().trim();
			if (prompt) {
				const textField = this.dom.find("#taskAiValidationPrompt");
				const currentPrompt = textField.val() as string;
				textField.val(currentPrompt ? `${currentPrompt} ${prompt}` : prompt); // Append instead of overwriting to allow for multiple prompts
				// Close the dropdown
				this.dom.find("#aiValidationPromptDropdown").dropdown("toggle");
			}
		});
	}

	private updateAIValidationPromptVisibility() {
		const taskBookable = this.dom.find("#taskBookable").val() === "true";
		const descriptionRequired = this.dom.find(`input[name="descriptionRequired"]`).is(":checked");
		const shouldShow = taskBookable && descriptionRequired;

		this.dom.find(".ai-validation-prompt-component").toggle(shouldShow);
	}

	private initPositionTabFields(): void {
		this.locationPanel = document.getElementById("taskAdminLocationPanel") as LocationPanel | null ?? undefined;
		if (!this.locationPanel) return;

		const panel = this.locationPanel;

		panel.addEventListener(LocationPanel.EVENT_POSITION_CHANGED, () => {
			this.locationInherited = false;
		});

		this.dom.find("#taskOverrideAddress").change((event) => {
			const override = $(event.target).prop("checked") as boolean;
			this.locationInherited = !override;
			panel.setReadonly(!override);
			if (!override && this.parentTaskLocationData) {
				panel.setData(this.buildLocationPanelData(this.parentTaskLocationData));
			}
		});
	}

	protected updateHoursPlannedTotal(hoursPlanned: number | null) {
		if (this.hoursPlannedForSubtasks === -1) {
			this.dom.find("#hoursPlannedTotal").html("??");
		} else {
			if (hoursPlanned === null) {
				hoursPlanned = this.dom.find("#hoursPlanned").val() ? parseDecimalNumber(this.dom.find("#hoursPlanned").val() as string) : 0;
			}
			this.dom.find("#hoursPlannedTotal").html(formatDecimalNumber(hoursPlanned + this.hoursPlannedForSubtasks));
		}
	}

	private applyBudgetPlanningTypeToCalculator(budgetPlanningType: BudgetPlanningType | null | undefined) {
		// Inherited is not a real calculation mode — resolve it to the parent's effective type.
		const effective = budgetPlanningType === BudgetPlanningType.Inherited
			? this.budgetPlanningTypeParent
			: (budgetPlanningType ?? null);
		this.budgetCalculator.setBudgetPlanningType(effective);
	}

	protected checkHoursPlannedExceedanceWarning() {
		let displayWarning = false;
		if (this.isTaskLeader && SecurityStatus.isBudgetsAllowed()) {
			const hoursPlannedExceedance = this.budgetCalculator.getHoursPlannedManually() > this.budgetCalculator.getHoursPlanned();
			displayWarning = hoursPlannedExceedance && this.budgetCalculator.getBudgetPlanningType() !== BudgetPlanningType.None &&
				this.getBudgetPlanningType() !== BudgetPlanningType.Inherited;
			this.dom.find("#hoursPlannedExceedanceWarning").toggle(displayWarning);
		}
		return !displayWarning;
	}

	protected updateBudgetPlannedTotal(budgetPlanned: number | null) {
		if (this.budgetPlannedForSubtasks === -1) {
			this.dom.find("#budgetPlannedTotal").html("??");
		} else {
			if (budgetPlanned === null) {
				budgetPlanned = this.dom.find("#budgetPlanned").val() ? parseDecimalNumber(this.dom.find("#budgetPlanned").val() as string) : 0;
			}
			this.dom.find("#budgetPlannedTotal").html(formatDecimalNumber(budgetPlanned + this.budgetPlannedForSubtasks));
		}
	}

	private getBudgetPlanningType(): BudgetPlanningType {
		return this.dom.find("#budgetPlanningTypeSelect").val() as BudgetPlanningType;
	}

	private handleBudgetPlanningType(budgetPlanningType: BudgetPlanningType) {
		const hasPlanningControls = budgetPlanningType !== BudgetPlanningType.None && budgetPlanningType !== BudgetPlanningType.Inherited;

		if (budgetPlanningType === BudgetPlanningType.Inherited) {
			this.dom.find("#budgetPlanningType").val(this.budgetPlanningTypeParent);
			this.dom.find("#budgetPlanningTypeInherited").val("true");
			this.budgetCalculator.setHourlyRate(this.hourlyRateParent);
			this.dom.find("#hourlyRate").prop("readonly", false);
			this.dom.find("#budgetPlanningTypeSelect").addClass("select-width-inherited");
		} else {
			this.dom.find("#budgetPlanningType").val(budgetPlanningType);
			this.dom.find("#budgetPlanningTypeInherited").val("false");
			this.dom.find("#hourlyRate").prop("readonly", false);
			this.dom.find("#budgetPlanningTypeSelect").removeClass("select-width-inherited");
		}

		this.dom.find("#hoursPlannedRow").toggle(hasPlanningControls);

		this.dom.find("#hourlyRateRow").toggle(budgetPlanningType === BudgetPlanningType.TaskHourlyRate || budgetPlanningType === BudgetPlanningType.FixedPrice);

		this.dom.find("#budgetPlannedRow").toggle(hasPlanningControls);

		if (budgetPlanningType === BudgetPlanningType.TaskHourlyRate) {
			this.dom.find("#hoursPlannedRow").insertAfter("#hourlyRateRow");

			this.dom.find("#budgetPlannedRow").insertAfter("#hoursPlannedRow");

			this.dom.find("#hourlyRateLabel").text(messages["task.hourlyRate"]);
			this.dom.find("#budgetPlannedLabel").text(messages["task.budgetPlanned"]);
		} else if (budgetPlanningType === BudgetPlanningType.UserHourlyRate) {
			this.dom.find("#budgetPlannedRow").insertAfter("#hoursPlannedRow");

			this.dom.find("#hourlyRateRow").insertAfter("#budgetPlannedRow");

			this.dom.find("#hourlyRateLabel").text(messages["task.hourlyRate"]);
			this.dom.find("#budgetPlannedLabel").text(messages["task.budgetPlanned"]);
		} else if (budgetPlanningType === BudgetPlanningType.FixedPrice) {
			this.dom.find("#hoursPlannedRow").insertAfter("#budgetPlannedRow");

			this.dom.find("#hourlyRateRow").insertAfter("#hoursPlannedRow");

			this.dom.find("#hourlyRateLabel").text(messages["task.hourlyRatePlanned"]);
			this.dom.find("#budgetPlannedLabel").text(messages["task.budget"]);
		}

		const budgetCountNotBillableHoursDiv = this.dom.find("#budgetCountNotBillableHoursDiv");
		if (budgetPlanningType === BudgetPlanningType.None) {
			budgetCountNotBillableHoursDiv.toggle(false)
				.find("input").prop("disabled", true);
		} else if (budgetPlanningType === BudgetPlanningType.Inherited) {
			budgetCountNotBillableHoursDiv.toggle(true)
				.find("input")
				.prop("checked", this.budgetCountNotBillableHoursParent)
				.prop("disabled", true);
		} else {
			budgetCountNotBillableHoursDiv.toggle(true)
				.find("input").prop("disabled", false);
		}

		this.showTaskAdvancedFields();
	}

	public edit(taskDetails: TaskDetailsResponse) {
		this.isTaskLeader = globals.isAdmin || taskDetails.task.currentUserIsLeader && SecurityStatus.isCorporateFeaturesAllowed();
		this.taskId = taskDetails.task.id;
		this.taskUUID = taskDetails.task.taskId;

		// get the task path; not html encoded ("parent/child" -> "parent")
		this.taskPath = taskDetails.task.breadCrumbs.substring(0, taskDetails.task.breadCrumbs.length - taskDetails.task.name.length - 1);
		this.setDialogMode(TaskDialogMode.Edit, taskDetails);
		this.editTask(taskDetails);

		this.originalHourlyRate = taskDetails.task.hourlyRate || 0;
		this.originalBudgetPlanningType = taskDetails.task.budgetPlanningType;

		this.leaderAssignmentPanel?.reloadAssigned();
		this.taskAssignmentPanel.reloadAssigned();

		this.resetAttachmentsTab();

		this.showTaskAdvancedFields();
		this.showDialog();
	}

	public addSubtask(taskDetails: TaskDetailsResponse) {
		const parent = taskDetails.task;

		this.isTaskLeader = globals.isAdmin || globals.isTaskLeader && SecurityStatus.isCorporateFeaturesAllowed();
		this.taskId = null;
		this.taskUUID = null;

		this.taskPath = parent.breadCrumbs;
		this.setDialogMode(TaskDialogMode.Subtask);
		this.initFields();
		this.dom.find("#parentTaskId").val(parent.id);
		this.dom.find(`input[name="billable"]`).prop("checked", parent.billable);

		// Inherit fields by default for subtask
		this.setLocationTabFields({
			locationInherited: true,
			parentTask: parent,
			longitude: parent.longitude,
			latitude: parent.latitude,
			zipCode: parent.zipCode,
			address: parent.address,
			city: parent.city,
			state: parent.state,
			countryId: parent.countryId,
			locationRestrictionInherited: true,
			locationRestrictionRadius: parent.locationRestrictionRadius,
		});

		this.setTaskSettings(parent);

		this.leaderAssignmentPanel?.reloadAssigned();
		this.taskAssignmentPanel.reloadAssigned();

		this.resetAttachmentsTab();

		this.showTaskAdvancedFields();
		this.showDialog();
	}

	public create() {
		this.isTaskLeader = globals.isAdmin || globals.isTaskLeader && SecurityStatus.isCorporateFeaturesAllowed();
		this.taskId = null;
		this.taskUUID = null;

		this.taskPath = "";
		this.setDialogMode(TaskDialogMode.Create);
		this.resetBudgetFields();
		this.initForm();
		this.hideAssignmentTables();

		// Don't inherit fields for new task
		this.setLocationTabFields({
			locationInherited: false,
			locationRestrictionInherited: false,
		});

		this.leaderAssignmentPanel?.reloadAssigned();
		this.taskAssignmentPanel.reloadAssigned();

		this.resetAttachmentsTab();

		this.showTaskAdvancedFields();
		this.showDialog();
	}

	protected hideAssignmentTables() {
		this.dom.find("#taskProjectLeaders").closest(".row").hide();
		this.dom.find("#taskUserAssignments").closest(".row").hide();
	}

	private updateFixedBudgetInfo(totalHoursPlanned: number, userHoursPlanned: number) {

		const spanFixedBudgetInfoLine1 = this.dom.find("#fixedBudgetInfoLine1");
		const spanFixedBudgetInfoLine2 = this.dom.find("#fixedBudgetInfoLine2");
		const planningTypeInherited = this.dom.find("#budgetPlanningTypeInherited").val() === "true";

		if (!totalHoursPlanned || planningTypeInherited || userHoursPlanned === 0) {
			spanFixedBudgetInfoLine1.text(" ");
			spanFixedBudgetInfoLine2.text(" ");
			return;
		}

		// add/remove css class for error style
		if (userHoursPlanned > totalHoursPlanned) {
			spanFixedBudgetInfoLine1.addClass("text-danger");
			spanFixedBudgetInfoLine2.addClass("text-danger");
		} else {
			spanFixedBudgetInfoLine1.removeClass("text-danger");
			spanFixedBudgetInfoLine2.removeClass("text-danger");
		}
		spanFixedBudgetInfoLine1.text(messages.withArguments("admin.assignment.info.hoursPlanned", userHoursPlanned, totalHoursPlanned));
		spanFixedBudgetInfoLine2.text(messages.withArguments("admin.assignment.info.hoursPlanned.remaining", totalHoursPlanned - userHoursPlanned));
	}

	private initTaskProjectLeaders() {
		const userSource = api("admin/getAssignableUsers.json")
			.get({
				taskLeaderOnly: true
			})
			.then(response => response.data.map(toAssignableUser));

		const assignedLeaders = (): JQueryPromise<AssignedUser[]> => {
			const taskId = $("#taskId").val();

			// For a new subtask we load the parents leaders as autoAssigned users
			const loadTaskId = Number(taskId || $("#parentTaskId").val());

			if (loadTaskId) {
				return api("tasks/loadTaskLeaders.json")
					.get({
						taskId: loadTaskId
					})
					.then(users => {
						if (!taskId) {
							users.forEach(user => {
								user.autoAssigned = true;
							});
						}

						return users;
					});
			} else {
				return promiseNow<AssignedUser[]>([]);
			}
		};

		this.leaderAssignmentPanel = new UserAssignmentPanel({
			panelElement: $("#leaderAssignmentPanel"),
			selectSourceProvider: () => userSource,
			assignedSourceProvider: assignedLeaders,
			editMessage: "admin.task.assign.leader"
		});
	}

	private initTaskAssignments() {
		const userSource = () => {
			return api("admin/getAssignableUsers.json").get()
				.then(response => response.data.map(toAssignableUser));
		};

		const assignedTo = () => {
			const taskId = $("#taskId").val();

			// For a new subtask we load the parents leaders as autoAssigned users
			const loadTaskId = taskId || $("#parentTaskId").val();

			const promise: JQueryPromise<(AssignedUser | AssignedGroup)[]> = loadTaskId
				? api("tasks/loadTaskAssignments.json")
					.get({
						taskId: loadTaskId
					})
					.then(data => {
						const assigned = data.data;
						if (!taskId) {
							assigned.forEach(userOrGroup => {
								userOrGroup.autoAssigned = true;
							});
						}

						return assigned;
					})
				: promiseNow<(AssignedUser | AssignedGroup)[]>([]);

			return promise.then(data => {
				this.budgetCalculator.setUserRecords(data);

				this.updateAllAssignedWarningStyle();

				return data;
			});
		};

		this.taskAssignmentPanel = new TaskUserGroupAssignmentPanel({
			panelElement: $("#userGroupAssignmentPanel"),
			selectSourceProvider: userSource,
			assignedSourceProvider: assignedTo,
			editMessage: SecurityStatus.isCorporateFeaturesAllowed() ? "admin.task.assign.usergroups" : "admin.task.assign.user"
		}, () => {
			this.budgetCalculator.updateHoursPlannedPerUser();
		});

		const onAssignmentChange = () => {
			this.budgetCalculator.setUserRecords(this.taskAssignmentPanel.getAssigned());
			this.updateAllAssignedWarningStyle();
		};

		this.taskAssignmentPanel.onUnassignment(onAssignmentChange);
		this.taskAssignmentPanel.onAssignment(onAssignmentChange);
	}

	protected editTask(taskDetails: TaskDetailsResponse) {
		const task = taskDetails.task;
		this.dom.find("#taskId").val(task.id);
		if (task.parentTask?.id) {
			this.dom.find("#parentTaskId").val(task.parentTask.id);
		} else {
			this.dom.find("#parentTaskId").val("");
		}
		this.dom.find("#taskVersion").val(task.version);
		this.dom.find("#taskName").val(task.name);
		this.dom.find("#taskDescription").val(task.description);
		this.dom.find("#taskExternalComments").val(task.externalComments);
		this.dom.find("#taskAiValidationPrompt").val(task.aiValidationPrompt);
		const start = parseISODate(task.start);
		this.dom.find("#taskStart").val(formatDate(start));
		const end = parseISODate(task.end);
		this.dom.find("#taskEnd").val(formatDate(end));
		this.dom.find("#taskEarliestStartTime").val(formatTime(parseISOTime(task.earliestStartTime)));
		this.dom.find("#taskEarliestStartTimeField").val(task.earliestStartTime);
		this.dom.find("#taskLatestEndTime").val(formatTime(parseISOTime(task.latestEndTime)));
		this.dom.find("#taskLatestEndTimeField").val(task.latestEndTime);
		this.dom.find("#taskEarliestStartTime, #taskLatestEndTime").prop("disabled", !task.bookable);
		this.dom.find("#taskLockDatePanel").show();
		this.dom.find("#taskClearLock").val(false.toString());
		this.dom.find("#taskExternalId").val(task.externalId);
		this.dom.find("#customTaskField1").val(task.customField1);
		this.dom.find("#customTaskField2").val(task.customField2);
		this.dom.find("#customTaskField3").val(task.customField3);
		this.dom.find(`select[name="bookable"]`).val(String(task.bookable));
		this.dom.find("#descriptionRequiredDiv").toggle(task.bookable);

		this.setTaskClearButtons();
		this.setTaskLockDate(task);
		this.setCheckboxes(task);
		this.setTaskSettings(task, taskDetails.hoursPlannedForSubtasks, taskDetails.budgetPlannedForSubtasks);
		this.setLocationTabFields(task);
	}

	protected setTaskSettings(task: Task, hoursPlannedForSubtasks?: number, budgetPlannedForSubtasks?: number) {
		if (this.dom.find("#taskId").val()) {
			this.budgetPlanningTypeParent = task.parentBudgetPlanningType;
			this.budgetCountNotBillableHoursParent = task.parentBudgetCountNotBillableHours;
			this.hourlyRateParent = task.parentHourlyRate;
			const budgetPlanningType = this.setBudgetPlanningType(task.budgetPlanningType, this.budgetPlanningTypeParent,
				task.budgetPlanningTypeInherited, globals.isAdmin || task.parentTask?.currentUserIsLeader);
			this.applyBudgetPlanningTypeToCalculator(task.budgetPlanningType);
			this.budgetCalculator.reinitialize(task.hoursPlanned, task.hourlyRate, task.budgetPlanned);
			this.checkHoursPlannedExceedanceWarning();
			this.handleBudgetPlanningType(budgetPlanningType);
			this.hoursPlannedForSubtasks = hoursPlannedForSubtasks || 0;
			this.budgetPlannedForSubtasks = budgetPlannedForSubtasks || 0;
			this.updateHoursPlannedTotal(null);
			this.updateBudgetPlannedTotal(null);
		} else if (this.dom.find("#parentTaskId").val()) {
			this.budgetPlanningTypeParent = task.budgetPlanningType;
			this.budgetCountNotBillableHoursParent = task.budgetCountNotBillableHours;
			this.hourlyRateParent = task.hourlyRate;
			const budgetPlanningType = this.setBudgetPlanningType(task.budgetPlanningType, this.budgetPlanningTypeParent, true,
				globals.isAdmin || task.currentUserIsLeader);
			this.applyBudgetPlanningTypeToCalculator(task.budgetPlanningType);
			this.budgetCalculator.reinitialize(0, task.hourlyRate, 0);
			this.checkHoursPlannedExceedanceWarning();
			this.handleBudgetPlanningType(budgetPlanningType);
			this.hoursPlannedForSubtasks = 0;
			this.budgetPlannedForSubtasks = 0;
			this.updateHoursPlannedTotal(null);
			this.updateBudgetPlannedTotal(null);
		} else {
			this.hoursPlannedForSubtasks = 0;
			this.budgetPlannedForSubtasks = 0;
		}

		this.dom.find("#taskExternalComments").val(task.externalComments);
		this.setTaskLockDate(task);
	}

	/**
	 * Sets the planning type of the task. There are four "standard" types: None, TaskHourlyRate, UserHourlyRate, FixedPrice.
	 * Another type is Inherited, which is added only if the task has a parent task
	 * If the user has the permission the Inherited option will show the the type of the parent task e.g. "Planned by parent ('Fixed Price')"
	 * Otherwise it will be just: "Planned by parent"
	 * @param budgetPlanningType budget planning type of the selected task
	 * @param parentBudgetPlanningType budget planning type of the parent task
	 * @param budgetPlanningTypeInherited true if the planning type is inherited of the parent task
	 * @param showInheritedPlanningType true
	 * @protected
	 */
	protected setBudgetPlanningType(budgetPlanningType: BudgetPlanningType, parentBudgetPlanningType: BudgetPlanningType,
	                                budgetPlanningTypeInherited: boolean, showInheritedPlanningType: boolean = false): BudgetPlanningType {
		const budgetPlanningTypeSelect = this.dom.find("#budgetPlanningTypeSelect");
		budgetPlanningTypeSelect.find("option").each((i, e) => {
			if (i > 3) {
				$(e).remove();
			}
		});
		if (parentBudgetPlanningType !== null) {
			budgetPlanningTypeSelect.append($("<option></option>", {
				value: BudgetPlanningType.Inherited,
				// check if the user has permission to display the budget planning type of the parent task TIMR-6493
				text: showInheritedPlanningType ?
					messages.withArguments("task.budgetPlanningType.INHERITED", messages["task.budgetPlanningType." + parentBudgetPlanningType]) :
					messages["task.budgetPlanningType.INHERITED.hideType"]
			}));
		}
		if (budgetPlanningTypeInherited && parentBudgetPlanningType !== null) {
			budgetPlanningTypeSelect.val(BudgetPlanningType.Inherited);
			return BudgetPlanningType.Inherited;
		} else if (budgetPlanningType !== null) {
			budgetPlanningTypeSelect.val(budgetPlanningType);
			return budgetPlanningType;
		} else {
			budgetPlanningTypeSelect.val(BudgetPlanningType.None);
			return BudgetPlanningType.None;
		}
	}

	protected setCheckboxes(task: Task) {
		this.dom.find(`input[name="billable"]`).prop("checked", task.billable);
		this.dom.find(`input[name="descriptionRequired"]`).prop("checked", task.descriptionRequired);
		this.dom.find(`input[name="budgetCountNotBillableHours"][type="checkbox"]`).prop("checked", task.budgetCountNotBillableHours);
		// Set hidden field if budgetCountNotBillableHours setting is not configurable on per task basis
		this.dom.find(`input[name="budgetCountNotBillableHours"][type="hidden"]`).val(task.budgetCountNotBillableHours ? "" + task.budgetCountNotBillableHours : null);
	}

	protected setTaskClearButtons() {
		if (this.dom.find("#taskStart").val()) {
			this.dom.find("#taskStartClearButton").prop("disabled", false);
		}
		if (this.dom.find("#taskEnd").val()) {
			this.dom.find("#taskEndClearButton").prop("disabled", false);
		}
	}

	protected setTaskLockDate(task: Task) {
		const clearable = task?.lockDate !== undefined && task?.lockDate !== null;
		const lockDate = task?.lockDateInherited;
		const lockDateString = formatDate(parseISODate(lockDate));
		const taskLockDate = this.dom.find("#taskLockDate");
		taskLockDate.val(lockDateString);
		if (this.isTaskLeader && lockDateString && clearable) {
			this.dom.find("#taskLockDateClearButton").prop("disabled", false);
		} else {
			this.dom.find("#taskLockDateClearButton").prop("disabled", true);
		}
	}

	protected setDateTimes() {
		normalizeTimeInput(this.dom.find("#startDate"));
		normalizeTimeInput(this.dom.find("#endDate"));
		this.dom.find("#taskStartTime").val(getISODate(parseDateInput(this.dom.find("#taskStart").val() as string)));
		this.dom.find("#taskEndTime").val(getISODate(parseDateInput(this.dom.find("#taskEnd").val() as string)));
		this.dom.find("#taskEarliestStartTimeField").val(getISOTime(parseTimeInput(this.dom.find("#taskEarliestStartTime").val() as string)));
		this.dom.find("#taskLatestEndTimeField").val(getISOTime(parseTimeInput(this.dom.find("#taskLatestEndTime").val() as string)));
	}

	private setLocationTabFields(task: Partial<Task>) {
		const inheritedAndValid = task.locationInherited && task.parentTask != null;

		this.dom.find("#taskOverrideContentGroup").toggle(task.parentTask != null);

		this.parentTaskLocationData = task.parentTask;
		this.setHiddenLocationFields({
			address: task.address,
			city: task.city,
			zipCode: task.zipCode,
			state: task.state,
			countryId: task.countryId ?? this.config?.billingAddressCountryId ?? null,
			latitude: task.latitude,
			longitude: task.longitude,
			locationInherited: task.locationInherited ?? false,
			locationRestrictionRadius: task.locationRestrictionRadius,
			locationRestrictionInherited: task.locationRestrictionInherited ?? false,
		});
		this.locationPanel?.setParentRestrictionRadius(task.parentTask?.locationRestrictionRadius);

		if (task.parentTask?.locationRestrictionRadius) {
			this.locationPanel?.setInheritedRadiusLabel(
				messages.withArguments("task.restrictionRadius.inherited", task.parentTask.locationRestrictionRadius + "m")
			);
		} else {
			this.locationPanel?.setInheritedRadiusLabel(
				messages.withArguments("task.restrictionRadius.inherited", messages["locationPanel.restrictionRadius.none"])
			);
		}

		this.locationPanel?.setRestrictionRadiusSelection(task.locationRestrictionRadius, task.locationRestrictionInherited);

		this.dom.find("#taskOverrideAddress").prop("checked", !task.locationInherited);
		this.locationInherited = !!task.locationInherited;

		this.locationPanel?.setReadonly(inheritedAndValid);
		this.locationPanel?.setData(this.buildLocationPanelData(task));
	}


	private buildLocationPanelData(task: Partial<TaskLocationData>): LocationPanelData {
		return {
			latitude: task.latitude,
			longitude: task.longitude,
			address: task.address,
			city: task.city,
			zipCode: task.zipCode,
			state: task.state,
			countryId: task.countryId ?? this.config?.billingAddressCountryId,
			locationRestrictionRadius: task.locationRestrictionRadius,
		};
	}

	protected showDialog() {
		this.dom.find(".nav > li.active").removeClass("active in");
		this.dom.find(".tab-content > div.tab-pane.active").removeClass("active in");
		this.dom.find(".nav > li:first-child").addClass("active in");
		this.dom.find(".tab-content > div.tab-pane:first-child").addClass("active in");

		this.updateAIValidationPromptVisibility();

		this.dialog.show();
	}

	protected save(button: JQuery) {
		this.setDateTimes();

		// Sync location panel state into hidden form fields so serializeArray() picks them up.
		if (this.locationPanel) {
			const data = this.locationPanel.getData();
			this.setHiddenLocationFields({
				address: data.address,
				city: data.city,
				zipCode: data.zipCode,
				state: data.state,
				countryId: data.countryId,
				latitude: data.latitude,
				longitude: data.longitude,
				locationInherited: this.locationInherited,
				locationRestrictionRadius: data.locationRestrictionRadius,
				locationRestrictionInherited: data.locationRestrictionInherited ?? false,
			});
		}

		const formData: RequestParam[] = $("#taskAdministrationDialogForm").serializeArray();

		// Same for the budgetCountNotBillableHours checkbox that is disabled if the setting is inherited.
		formData.push(...FormUtil.serializeArrayDisabled(this.dom.find("#budgetCountNotBillableHours").parent()));

		if (this.leaderAssignmentPanel) {
			const leaders = this.leaderAssignmentPanel.getAssigned()
				.filter(user => !user.autoAssigned)
				.map(user => ({
					name: "leaders",
					value: user.id
				}));

			formData.push(...leaders);
		}

		const groups = this.taskAssignmentPanel.getAssigned()
			.filter(userOrGroup => userOrGroup.elementType === "GROUP")
			.map(group => ({
				name: `assignedToGroups`,
				value: group.id
			}));

		formData.push(...groups);

		const users: RequestParam[] = [];
		this.taskAssignmentPanel.getAssigned()
			.filter(userOrGroup => userOrGroup.elementType === "USER")
			.forEach((user: AssignedUser, index) => {
				const field = `assignedToUsers[${index}].`;

				users.push({
					name: field + "id",
					value: user.id
				}, {
					name: field + "hoursPlanned",
					value: user.hoursPlanned != null ? formatDecimalNumber(user.hoursPlanned) : null
				}, {
					name: field + "hoursPlannedManually",
					value: user.hoursPlanned != null
				}, {
					name: field + "recordingLock",
					value: user.recordingLock || RecordingLockType.Recordable
				});
			});
		formData.push(...users);

		const newFiles = this.taskAttachmentGallery?.getFilesToUpload() || [];
		const attachmentFormData = this.taskAttachmentGallery?.getSerializedArrayElements() || [];

		formData.push(...attachmentFormData);

		const saveTask = (updateRecordings: boolean = true) => {
			formData.push({name: "updateRecordings", value: updateRecordings});

			api("tasks/saveTask.json")
				.post(formData)
				.then(response => {
					SecurityStatus.update(response.securityStatus);
					$(document).triggerHandler(TaskAdministrationDialog.EVENT_TASK_SAVED, response.task);

					// Notifies the backend of the successful upload, no need to wait for the response / handle the promise
					// We want to confirm each individual upload as soon as it is done and not wait for all of them
					const confirmCallback = (attachmentId: string) => {
						void api("tasks/confirmTaskAttachmentUpload.json")
							.post({
								attachmentId: attachmentId
							});
					}

					// Upload to S3 once the backend has generated the upload URLs
					return AttachmentUtil.uploadToS3(newFiles, response.attachmentUploadUrls, confirmCallback);
				})
				.then(() => {
					this.closeDialog();
					Button.resetLoading(button);
				})
				.catch((response: ErrorResponse) => {
					Button.resetLoading(button);
					if (response.responseJSON?.fieldErrors) {
						FormUtil.markFieldErrors($("#taskAdministrationDialogForm"), response.responseJSON.fieldErrors, true);
					}
				});
		};

		this.showDeleteAttachmentsConfirmDialog()
			.then(() => this.checkIfChangedHourlyRateShouldUpdateRecordings())
			.done(saveTask)
			.fail(() => {
				Button.resetLoading(button);
			});
	}


	private setHiddenLocationFields(fields: {
		address?: string | null;
		city?: string | null;
		zipCode?: string | null;
		state?: string | null;
		countryId?: number | null;
		latitude?: number | null;
		longitude?: number | null;
		locationInherited: boolean;
		locationRestrictionRadius?: number | null;
		locationRestrictionInherited: boolean;
	}): void {
		const toStr = (value?: string | number | boolean | null): string => value == null ? "" : String(value);
		const setField = (id: string, value: string) => this.dom.find(id).val(value);

		setField("#taskAddress", toStr(fields.address));
		setField("#taskCity", toStr(fields.city));
		setField("#taskZipCode", toStr(fields.zipCode));
		setField("#taskState", toStr(fields.state));
		setField("#taskCountry", toStr(fields.countryId));
		setField("#taskLatitude", toStr(fields.latitude));
		setField("#taskLongitude", toStr(fields.longitude));
		setField("#taskLocationInherited", toStr(fields.locationInherited));
		setField("#taskLocationRestrictionRadius", toStr(fields.locationRestrictionRadius));
		setField("#taskLocationRestrictionRadiusInherited", toStr(fields.locationRestrictionInherited));
	}

	private checkIfChangedHourlyRateShouldUpdateRecordings(): JQueryPromise<boolean> {
		const deferred: JQueryDeferred<boolean> = $.Deferred();

		if (!this.taskId) {
			deferred.resolve(false);
			return deferred.promise();
		}

		const hourlyRate = parseDecimalNumber(this.dom.find("#hourlyRate").val() as string);
		const budgetPlanningType = this.dom.find("#budgetPlanningType").val() as BudgetPlanningType;
		const hourlyRateChanged = (budgetPlanningType === BudgetPlanningType.TaskHourlyRate || budgetPlanningType === BudgetPlanningType.FixedPrice)
			&& hourlyRate !== this.originalHourlyRate;


		if (SecurityStatus.isBudgetsAllowed() && this.isTaskLeader
			&& (budgetPlanningType !== this.originalBudgetPlanningType || hourlyRateChanged)
		) {
			api("tasks/checkIfProjectTimeExistsForTask.json")
				.get({
					id: this.taskId
				})
				.done(data => {
					if (data.foundRecordings > 0) {
						BootstrapModal.confirm({
							title: "dialog.update.recordings.title",
							message: "dialog.update.recordings.message",
							yesLabel: "dialog.update.recordings.yes",
							noLabel: "dialog.update.recordings.no",
							yesAction: (modal) => {
								deferred.resolve(true);
								modal.close();
							},
							noAction: (modal) => {
								deferred.resolve(false);
								modal.close();
							},
							onClose: (modal, reason) => {
								// reject update if confirmation dialog is closed by 'x' (CLOSE_REASON_INTERNAL), without this onClose definition noAction would be called
								if (reason === BootstrapModal.CLOSE_REASON_INTERNAL) {
									deferred.reject();
								}
							}
						});
					} else {
						deferred.resolve(false);
					}
				})
				.fail(() => {
					deferred.reject()
				});
		} else {
			deferred.resolve(false);
		}
		return deferred.promise();
	}

	private showDeleteAttachmentsConfirmDialog(): JQueryPromise<void> {
		const deferred: JQueryDeferred<void> = $.Deferred();

		if (this.taskAttachmentGallery && this.taskAttachmentGallery.getAttachmentsToDelete().length > 0) {
			const fileNames = this.taskAttachmentGallery.getAttachmentsToDelete().map((attachment) => attachment.metadata.fileName).join(", ");

			BootstrapModal.confirm({
				message: messages.withArguments("attachments.delete.confirmation", fileNames),
				yesAction: (modal) => {
					deferred.resolve();
					modal.close()
				},
				noAction: (modal) => {
					deferred.reject();
					modal.close();
				}
			});
		} else {
			deferred.resolve();
		}

		return deferred.promise();
	}

	private resetAttachmentsTab() {
		this.taskAttachmentGallery?.reset();
		this.inheritedAttachmentGallery?.reset();
		this.attachmentsLoaded = false;
	}

	private updateAttachmentsTab() {
		if (this.attachmentsLoaded) {
			return;
		}

		this.loadAttachments(this.taskId);
		this.attachmentsLoaded = true;
	}

	private loadAttachments(taskId: number) {
		if (!this.taskAttachmentGallery || !this.inheritedAttachmentGallery || !taskId) {
			this.toggleInheritedAttachmentsSection(false);
			return;
		}

		this.taskAttachmentGallery.reset();
		this.inheritedAttachmentGallery.reset();

		api("tasks/getTaskAttachments.json")
			.get({
				taskId
			})
			.done((data: TaskAttachmentDownloadUrlResponse[]) => {
				const directAttachments = data.filter(attachment => attachment.inheritedFromTaskId == null);
				const inheritedAttachments = data.filter(attachment => attachment.inheritedFromTaskId != null);

				this.taskAttachmentGallery.setExistingFiles(directAttachments);

				this.toggleInheritedAttachmentsSection(inheritedAttachments.length > 0);

				this.inheritedAttachmentGallery.setExistingFiles(inheritedAttachments);
			});
	}

	protected deleteTask() {
		const deleteTaskData = {
			id: this.dom.find("#taskId").val(),
			name: this.dom.find("#taskName").val()
		};
		$(document).triggerHandler(TaskAdministrationDialog.EVENT_TASK_DELETED, [deleteTaskData]);
	}

	public closeDialog() {
		this.dialog.close();
	}

	protected resetBudgetFields() {
		this.budgetPlanningTypeParent = null;
		this.hourlyRateParent = undefined;
		this.budgetCountNotBillableHoursParent = null;
		this.setBudgetPlanningType(BudgetPlanningType.None, this.budgetPlanningTypeParent, false);
		this.originalBudgetPlanningType = BudgetPlanningType.None;
		this.budgetCalculator.resetValues();
		this.originalHourlyRate = 0;
		this.hoursPlannedForSubtasks = 0;
		this.budgetPlannedForSubtasks = 0;
		this.updateHoursPlannedTotal(0);
		this.updateBudgetPlannedTotal(0);
		this.handleBudgetPlanningType(BudgetPlanningType.None);
	}

	private setDialogMode(dialogMode: TaskDialogMode, data?: TaskDetailsResponse) {
		this.taskDialogMode = dialogMode;
		const title = this.buildTitle(data?.task?.name);
		this.dialog.setTitle(title, true);
		this.updateTaskLink(data?.task?.name);
		this.dialog.toggleButton("deleteButton", dialogMode === TaskDialogMode.Edit);
	}

	private resizeAssignmentPanels() {
		const screenHeight = $(window).height();

		// Estimated height of other dialog components + padding + margin ...
		const panelHeight = Math.max(screenHeight - 400, 500);
		this.leaderAssignmentPanel?.setScrollHeight(panelHeight);
		this.taskAssignmentPanel.setScrollHeight(panelHeight - 170);
	}

	private updateAllAssignedWarningStyle() {
		const assigned = this.taskAssignmentPanel.getAssigned();
		$("#showAllTasksWarning").toggleClass("alert-info", assigned.length === 0)
			.toggleClass("alert-warning", assigned.length !== 0);
	}

	private showTaskAdvancedFields() {
		const showBudgetFeature = SecurityStatus.isBudgetsAllowed() && this.isTaskLeader;
		const budgetPlanningType = this.getBudgetPlanningType();
		const tabTitle = showBudgetFeature ? messages["task.tab.assignedTo.withBudget"] : messages["task.tab.assignedTo"];
		$(`a[href="#taskAdministrationUserAssignments"]`).text(tabTitle);
		$(".task-field-budgetFeature").toggle(showBudgetFeature);
		$("#taskDescriptionColumn")
			.toggleClass("col-lg-12", !showBudgetFeature)
			.toggleClass("col-lg-6", showBudgetFeature);

		this.taskAssignmentPanel.setBudgetPlanningTypeInformation(budgetPlanningType, showBudgetFeature);

		const showCorporateFeatures = SecurityStatus.isCorporateFeaturesAllowed() && this.isTaskLeader;
		$(".task-field-corporateFeature").toggle(showCorporateFeatures);
		$('a[href="#taskAdministrationProjectLeaders"]').toggle(showCorporateFeatures);
	}

	private buildTitle(taskName?: string) {
		// taskName cannot be undefined, null, or empty
		const name: string = taskName || "\xa0";

		const breadCrumbs = this.taskPath ? this.taskPath + "/" + name : name;

		return $("<div>").addClass("flex-row")
			.append($("<h4>").addClass("flex-column m-0 text-nowrap")
				.text(messages[TaskAdministrationDialog.TITLE[this.taskDialogMode]] + ": ")
			)
			.append($("<div>").addClass("flex-column-auto ml-5 mt-auto mb-auto")
				.attr("title", breadCrumbs)
				.append($("<h4>").addClass("m-0 overflow-ellipsis")
					.html(htmlEscape(name))
				)
				.append($("<h6>").addClass("text-muted m-0 overflow-ellipsis").text(this.taskPath)))
			.prop("outerHTML") as string;
	}

	private updateTaskLink(taskName?: string) {
		const showCopyLink = this.taskUUID && taskName && this.taskDialogMode === TaskDialogMode.Edit;

		if (showCopyLink) {
			const breadCrumbs = this.taskPath
				? this.taskPath + "/" + taskName
				: taskName;

			const url = EntityLinks.getTaskLink(this.taskUUID, breadCrumbs)
			this.dom.find(".task-link-copy").show().attr("href", url)
		} else {
			this.dom.find(".task-link-copy").hide().attr("href", "#");
		}
	}

	private toggleInheritedAttachmentsSection(toggle: boolean) {
		const inheritedSection: HTMLFieldSetElement = document.querySelector("#taskAdministrationAttachments > .inherited-attachments");
		if (inheritedSection) {
			inheritedSection.style.display = toggle ? "block" : "none";
		}
	}
}
